import React, { useEffect, useState } from 'react';
import { FileText, Download, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { StudentLayout } from '../../components/layout/StudentLayout';
import { studentExamApi } from '../../api/examApi';
import { toast } from 'sonner';
import { ViewCorrectedPaperButton } from '../../components/student/CorrectedPaperViewer';

const STATUS_CFG = {
  in_progress: { label: 'In Progress', icon: Clock,         color: 'text-amber-600',  bg: 'bg-amber-100 dark:bg-amber-900/30' },
  submitted:   { label: 'Submitted',   icon: FileText,       color: 'text-blue-600',   bg: 'bg-blue-100 dark:bg-blue-900/30' },
  evaluated:   { label: 'Evaluated',   icon: CheckCircle,    color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  not_started: { label: 'Not Started', icon: AlertCircle,    color: 'text-slate-500',  bg: 'bg-slate-100 dark:bg-slate-700' },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function StudentResultsPage() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    studentExamApi.getMyResults()
      .then((res) => setResults(res.data))
      .catch(() => toast.error('Failed to load results'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (apiFn, filename) => {
    try {
      const res = await apiFn();
      downloadBlob(res.data, filename);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <StudentLayout title="My Results">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">My Results</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            All your exam attempts and scores
          </p>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 rounded-xl bg-slate-100 dark:bg-slate-700" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-12 text-center">
            <FileText size={40} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
            <p className="text-slate-500 dark:text-slate-400">No exam attempts yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {results.map((r) => {
              const cfg = STATUS_CFG[r.status] || STATUS_CFG.not_started;
              const StatusIcon = cfg.icon;
              const pct = r.total_score != null && r.total_marks
                ? ((r.total_score / r.total_marks) * 100).toFixed(1)
                : null;

              return (
                <div
                  key={r.attempt_id}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">{r.exam_title}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{r.exam_subject}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                        {new Date(r.attempted_at).toLocaleDateString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon size={12} /> {cfg.label}
                      </span>
                      {r.violation_count > 0 && (
                        <span className="rounded-full bg-rose-100 dark:bg-rose-900/30 px-2.5 py-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
                          {r.violation_count} violation{r.violation_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Scores */}
                  {r.status === 'evaluated' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-3 text-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400">MCQ</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                          {r.mcq_score ?? '—'}
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-3 text-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Subjective</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                          {r.subjective_score ?? '—'}
                        </p>
                      </div>
                      <div className="rounded-lg bg-primary/10 dark:bg-primary/20 p-3 text-center">
                        <p className="text-xs text-primary dark:text-accent-cool">Total</p>
                        <p className="text-lg font-bold text-primary dark:text-accent-cool">
                          {r.total_score}/{r.total_marks}
                        </p>
                        {pct && (
                          <p className="text-xs text-primary/70 dark:text-accent-cool/70">({pct}%)</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Feedback */}
                  {r.feedback && (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10 p-3">
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
                        Faculty Feedback
                      </p>
                      <p className="text-sm text-slate-700 dark:text-slate-300">{r.feedback}</p>
                    </div>
                  )}

                  {/* Downloads */}
                  {(r.submissions.length > 0 || r.corrected_paper_url) && (
                    <div className="flex flex-wrap gap-2">
                      {r.submissions.map((s, i) => (
                        <button
                          key={s.id}
                          onClick={() =>
                            handleDownload(
                              () => studentExamApi.downloadMySubmission(s.id),
                              `submission_${i + 1}.pdf`
                            )
                          }
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <Download size={12} />
                          My Upload {r.submissions.length > 1 ? i + 1 : ''}
                          {s.is_late && (
                            <span className="ml-1 text-rose-500">(late)</span>
                          )}
                        </button>
                      ))}
                      {r.corrected_paper_url && <ViewCorrectedPaperButton attemptId={r.attempt_id} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
